import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, Cable, ChevronRight, CircleHelp, Globe, Monitor, Network, Pencil, Printer, Router, Server,
  ShieldAlert, Smartphone, Tv, Wifi,
} from 'lucide-react';
import { ago, bandBg, bandFg, hasAgent, isSilent, statusOf, worstFinding } from './model';
import { NO_AGENT_AREA, SHOWN_PER_GROUP, buildTopology, gatewayFor, groupOf, isWeak, signalBars, snmpLine } from './topology';

// Network Map, drawn like a network diagram: Internet → router → areas →
// access points (and switches known from SNMP) → devices. Areas start collapsed; opening one lists each
// access point's most important devices first, the rest behind "+ N more".
export const TRAFFIC_WINDOWS = [[24, '24 h'], [168, '7 d']];

const LINE = 'var(--surf-line-strong)';
const seg = (on) => ({
  background: on ? 'var(--surf-raised)' : 'transparent',
  borderColor: on ? 'var(--surf-line)' : 'transparent',
  color: on ? 'var(--ink-text)' : 'var(--ink-muted)',
});

const CATEGORY_ICON = {
  Printers: Printer, 'Network devices': Network, 'TVs, cameras & IoT': Tv, 'Phones & tablets': Smartphone,
  Servers: Server, Computers: Monitor, Unknown: CircleHelp,
};

// One square tile for every node, so the diagram reads as one family.
const Tile = ({ icon: Icon, size = 48, tone = 'neutral', dashed = false, children }) => {
  const tones = {
    neutral: { borderColor: 'var(--surf-line-strong)', background: 'var(--surf-raised)', color: 'var(--ink-text)' },
    accent: { borderColor: 'var(--accent)', background: 'var(--surf-raised)', color: 'var(--accent)' },
    danger: { borderColor: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)', color: 'var(--sev-crit-fg)' },
    ghost: { borderColor: 'var(--surf-line-strong)', background: 'transparent', color: 'var(--ink-muted)' },
  };
  return (
    <span
      className="relative shrink-0 box-border flex items-center justify-center border-[1.5px]"
      style={{ width: size, height: size, borderRadius: size * 0.31, borderStyle: dashed ? 'dashed' : 'solid', ...tones[tone] }}
    >
      {Icon && <Icon aria-hidden="true" className="w-[45%] h-[45%]" strokeWidth={1.7} />}
      {children}
    </span>
  );
};

const Node = ({ icon, tone, dashed, title, sub, ariaLabel, onClick }) => (
  <button type="button" onClick={onClick} aria-label={ariaLabel} className="flex items-center gap-3 text-left">
    <Tile icon={icon} tone={tone} dashed={dashed} />
    <span className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[13px] font-medium truncate">{title}</span>
      {sub && <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{sub}</span>}
    </span>
  </button>
);

const VLine = ({ h = 28 }) => <span aria-hidden="true" className="block w-[1.5px] mx-auto" style={{ height: h, background: LINE }} />;

const Bars = ({ dbm }) => {
  const n = signalBars(dbm);
  const weak = n === 1;
  return (
    <span aria-hidden="true" className="flex items-end gap-[2px] h-3 shrink-0">
      {[4, 7, 10, 12].map((h, i) => (
        <span key={h} className="w-[3px] rounded-[1px]" style={{ height: h, background: i < n ? (weak ? 'var(--sev-med-fg)' : 'var(--ink-muted)') : 'var(--surf-line-strong)' }} />
      ))}
    </span>
  );
};

const Chip = ({ tone = 'neutral', children }) => {
  const t = {
    crit: ['var(--sev-crit-bg)', 'var(--sev-crit-fg)'], high: ['var(--sev-high-bg)', 'var(--sev-high-fg)'],
    med: ['var(--sev-med-bg)', 'var(--sev-med-fg)'], good: ['var(--sev-good-bg)', 'var(--sev-good-fg)'],
    neutral: ['var(--surf-raised)', 'var(--ink-faint)'],
  }[tone];
  return (
    <span className="inline-flex items-center gap-[5px] h-[22px] px-2 rounded-md text-[11.5px] whitespace-nowrap" style={{ background: t[0] }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t[1] }} />{children}
    </span>
  );
};

const RiskMix = ({ s }) => {
  const parts = [['crit', s.crit], ['high', s.high], ['med', s.med], ['low', s.low]].filter(([, n]) => n);
  return (
    <span aria-hidden="true" className="flex gap-[2px] h-1.5">
      {parts.map(([b, n]) => <span key={b} className="rounded-sm" style={{ flex: n, background: bandFg(b) }} />)}
      {s.unk > 0 && <span className="rounded-sm box-border border border-dashed" style={{ flex: s.unk, borderColor: 'var(--ink-faint)' }} />}
    </span>
  );
};

const summaryChips = (s) => [
  s.findings > 0 && <Chip key="f" tone={s.crit || s.high ? 'crit' : 'high'}>{s.findings} finding{s.findings === 1 ? '' : 's'}</Chip>,
  s.mismatch > 0 && <Chip key="g" tone="crit">{s.mismatch} odd gateway</Chip>,
  s.weak > 0 && <Chip key="w" tone="med">{s.weak} weak signal</Chip>,
  s.late > 0 && <Chip key="l" tone="med">{s.late} late</Chip>,
].filter(Boolean);

// ── Device row ─────────────────────────────────────────────────────────────
const DeviceRow = ({ a, link, place, gateway, selected, dim, onSelect }) => {
  const agentless = !hasAgent(a);
  const weak = isWeak(link);
  const worst = worstFinding(a.findings);
  let sub;
  let subColor = 'var(--ink-faint)';
  if (link?.gateway_mismatch) { sub = `gateway ${link.gateway_ip} ≠ ${gateway?.ip || 'router'}`; subColor = 'var(--sev-crit-fg)'; }
  else if (agentless) sub = [a.vendor, a.ip, place && !place.wireless && place.port].filter(Boolean).join(' · ') || 'no agent';
  else if (isSilent(a)) { sub = a.last_seen ? `seen ${ago(a.last_seen)} ago` : 'never reported'; subColor = 'var(--sev-med-fg)'; }
  else if (link?.type === 'wifi') { sub = [link.signal_dbm != null && `${link.signal_dbm} dBm`, weak ? 'weak' : link.band].filter(Boolean).join(' · '); if (weak) subColor = 'var(--sev-med-fg)'; }
  else sub = a.ip || '—';
  const st = statusOf(a);
  return (
    <button
      type="button"
      id={`nm-dev-${a.id}`}
      onClick={() => onSelect(selected ? null : a.id)}
      aria-pressed={selected}
      aria-label={`${a.host}, ${a.risk == null ? 'not scored' : `risk ${Math.round(a.risk)}`}${a.findings?.total ? `, ${a.findings.total} open findings` : ''}, ${sub}`}
      className="w-full min-h-[46px] box-border flex items-center gap-2.5 px-2.5 py-1.5 rounded-[11px] border text-left"
      style={{
        background: selected ? 'var(--surf-raised)' : 'var(--surf-surface)',
        borderColor: selected ? 'var(--accent)' : link?.gateway_mismatch ? 'var(--sev-crit-fg)' : weak ? 'var(--sev-med-bg)' : 'var(--surf-line)',
        borderStyle: agentless ? 'dashed' : 'solid',
        opacity: dim ? 0.35 : 1,
      }}
    >
      <span
        className="w-7 h-7 shrink-0 box-border rounded-lg border-[1.5px] flex items-center justify-center font-mono text-[10.5px] font-semibold"
        style={{
          borderColor: a.band === 'unk' ? 'var(--surf-line-strong)' : bandFg(a.band),
          borderStyle: a.band === 'unk' ? 'dashed' : 'solid',
          background: a.band === 'unk' ? 'transparent' : bandBg(a.band),
          color: a.band === 'unk' ? 'var(--ink-faint)' : bandFg(a.band),
        }}
      >
        {a.risk == null ? '?' : Math.round(a.risk)}
      </span>
      <span className="flex-grow min-w-0 flex flex-col gap-px">
        <span className="flex items-center gap-1.5 font-mono text-[12px] min-w-0">
          {!agentless && <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.dot }} />}
          <span className="truncate">{a.unnamed ? a.ip : a.host}</span>
        </span>
        <span className="text-[10.5px] truncate" style={{ color: subColor }}>{sub}</span>
      </span>
      {link?.type === 'wifi' && <Bars dbm={link.signal_dbm} />}
      {worst && (
        <span className="min-w-[18px] h-[18px] box-border px-[5px] rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: bandFg(worst), color: 'var(--surf-bg)' }}>
          {a.findings.total}
        </span>
      )}
    </button>
  );
};

// ── A column: an access point, a wired segment or a no-agent category ──────
const restSummary = (rest) => {
  if (!rest.length) return '';
  const issues = rest.filter(a => a.findings?.total).length;
  return issues ? `${issues} with findings` : rest.every(a => a.band === 'low') ? 'all low · no findings' : 'no findings';
};

const Group = ({ id, head, line, members, tree, matches, selectedId, onSelect, expanded, onToggle }) => {
  const open = expanded.has(id);
  const shown = open ? members : members.slice(0, SHOWN_PER_GROUP);
  const rest = members.slice(SHOWN_PER_GROUP);
  return (
    <div className="flex flex-col gap-3 min-w-0">
      {head}
      <div className="ml-6 pl-4 flex flex-col gap-2 border-l-[1.5px]" style={{ borderColor: LINE, borderLeftStyle: line }}>
        {shown.map(a => (
          <DeviceRow key={a.id} a={a} link={tree.linkOf(a.id)} place={tree.placementOf(a.id)} gateway={gatewayFor(tree, a.location)} selected={a.id === selectedId} dim={!matches(a)} onSelect={onSelect} />
        ))}
        {members.length === 0 && <span className="text-[12px] py-2" style={{ color: 'var(--ink-faint)' }}>No devices reporting through it now.</span>}
        {rest.length > 0 && (
          <button type="button" onClick={() => onToggle(id)} aria-expanded={open} className="h-10 box-border flex items-center gap-2 px-3 rounded-[11px] border border-dashed text-[12px] text-left" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-muted)' }}>
            {open
              ? <span style={{ color: 'var(--ink-text)' }}>Show fewer</span>
              : <><span className="font-medium" style={{ color: 'var(--ink-text)' }}>+ {rest.length} more</span>· {restSummary(rest)}</>}
          </button>
        )}
      </div>
    </div>
  );
};

const ApHead = ({ ap, areaNames, editing, onEdit, onUpdate, onSelect }) => {
  const [form, setForm] = useState({ name: ap.name || '', area: ap.area || '' });
  useEffect(() => { if (editing) setForm({ name: ap.name || '', area: ap.area || '' }); }, [editing, ap.name, ap.area]);
  const save = (e) => { e.preventDefault(); onUpdate(ap.key, { name: form.name, area: form.area }); onEdit(null); };
  const bands = ap.bands?.length ? ap.bands.join(' + ') : (ap.ssids || []).join(', ');
  const health = snmpLine(ap.snmp);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        {ap.asset
          ? <button type="button" onClick={() => onSelect(ap.asset.id)} aria-label={`Details of ${ap.label}`} className="shrink-0"><Tile icon={ap.kind === 'switch' ? Network : Wifi} tone={ap.suspicious ? 'danger' : 'neutral'} dashed={ap.status !== 'known'} /></button>
          : <Tile icon={ap.kind === 'switch' ? Network : Wifi} tone={ap.suspicious ? 'danger' : 'neutral'} dashed={ap.status !== 'known'} />}
        <span className="flex-grow min-w-0 flex flex-col gap-0.5">
          <span className="text-[13px] font-medium truncate" style={{ color: ap.suspicious ? 'var(--sev-crit-fg)' : 'var(--ink-text)' }}>
            {ap.suspicious ? 'Unknown access point' : ap.label}
          </span>
          <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>
            {ap.suspicious ? `“${ap.ssids.join('”, “')}” · ${ap.key}` : `${ap.clients.length} device${ap.clients.length === 1 ? '' : 's'}${bands ? ` · ${bands}` : ''}`}
          </span>
          {health && <span className="text-[11px] truncate" style={{ color: health.warn ? 'var(--sev-med-fg)' : 'var(--ink-faint)' }}>{health.text}</span>}
        </span>
        {!ap.polled && <button type="button" onClick={() => onEdit(editing ? null : ap.key)} aria-label={`Rename ${ap.label} or set its area`} title="Rename or set area" className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
        </button>}
      </div>
      {ap.status !== 'known' && !editing && (
        <div className="flex items-center gap-2 pl-[60px]">
          <Chip tone={ap.suspicious ? 'crit' : 'neutral'}>{ap.suspicious ? 'not one of yours' : 'new'}</Chip>
          <button type="button" onClick={() => onUpdate(ap.key, { status: 'known' })} className="h-7 px-2.5 rounded-md border text-[12px]" style={{ borderColor: 'var(--surf-line-strong)' }}>
            {ap.suspicious ? "It's ours" : 'Accept as ours'}
          </button>
        </div>
      )}
      {editing && (
        <form onSubmit={save} className="m-0 flex flex-col gap-2 pl-[60px]">
          <input aria-label="Access point name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name, e.g. Office-2F" maxLength={64} className="h-9 px-2.5 rounded-lg border text-[13px] bg-transparent" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }} />
          <input aria-label="Area" list="nm-areas" value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} placeholder="Area, e.g. Floor 2" maxLength={64} className="h-9 px-2.5 rounded-lg border text-[13px] bg-transparent" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }} />
          <datalist id="nm-areas">{areaNames.map(n => <option key={n} value={n} />)}</datalist>
          <span className="flex gap-2">
            <button type="submit" className="h-8 px-3 rounded-lg text-[12.5px] font-semibold" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>Save</button>
            <button type="button" onClick={() => onEdit(null)} className="h-8 px-3 rounded-lg border text-[12.5px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>Cancel</button>
          </span>
        </form>
      )}
    </div>
  );
};

const PlainHead = ({ icon, title, sub, dashed }) => (
  <div className="flex items-center gap-3">
    <Tile icon={icon} tone={dashed ? 'ghost' : 'neutral'} dashed={dashed} />
    <span className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[13px] font-medium truncate">{title}</span>
      {sub && <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{sub}</span>}
    </span>
  </div>
);

// ── Areas ──────────────────────────────────────────────────────────────────
const AreaCard = ({ area, onOpen }) => {
  const s = area.summary;
  const noAgent = area.id === NO_AGENT_AREA;
  const apCount = area.aps.filter(ap => ap.kind === 'ap').length;
  const switchCount = area.aps.length - apCount;
  const rows = noAgent
    ? area.groups.map(g => [g.name, g.members.length])
    : [...area.aps.map(ap => [ap.suspicious ? 'Unknown access point' : ap.label, ap.clients.length, ap.suspicious]),
      ...(area.wired.length ? [['Wired', area.wired.length]] : []),
      ...(area.unlinked.length ? [['Link not reported', area.unlinked.length]] : [])];
  const tone = area.suspicious ? 'var(--sev-crit-fg)' : s.attention ? 'var(--sev-high-fg)' : null;
  return (
    <div className="h-full box-border flex flex-col gap-3 p-4 rounded-2xl border" style={{ background: noAgent ? 'transparent' : 'var(--surf-raised)', borderColor: tone || 'var(--surf-line)', borderStyle: noAgent ? 'dashed' : 'solid' }}>
      <div className="flex items-center gap-2.5">
        <Tile icon={noAgent ? CircleHelp : area.aps.length ? Wifi : Cable} size={36} tone={noAgent ? 'ghost' : 'neutral'} dashed={noAgent} />
        <span className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[14px] font-semibold truncate">{area.name}</span>
          <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>
            {noAgent ? `Seen on the network · ${s.total}` : [apCount && `${apCount} access point${apCount === 1 ? '' : 's'}`, switchCount && `${switchCount} switch${switchCount === 1 ? '' : 'es'}`, `${s.total} device${s.total === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
          </span>
        </span>
      </div>
      {noAgent ? <span aria-hidden="true" className="h-1.5 box-border rounded-sm border border-dashed" style={{ borderColor: 'var(--ink-faint)' }} /> : <RiskMix s={s} />}
      <div className="flex flex-col gap-1.5 text-[12.5px]">
        {rows.slice(0, 4).map(([name, n, bad]) => (
          <span key={name} className="flex justify-between gap-2">
            <span className="truncate" style={{ color: bad ? 'var(--sev-crit-fg)' : 'var(--ink-text)' }}>{name}</span>
            <span className="font-mono" style={{ color: 'var(--ink-faint)' }}>{n}</span>
          </span>
        ))}
        {rows.length > 4 && <span style={{ color: 'var(--ink-faint)' }}>+ {rows.length - 4} more</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {area.suspicious && <Chip tone="crit">unknown access point</Chip>}
        {noAgent
          ? (s.newThisWeek > 0 && <Chip>{s.newThisWeek} new this week</Chip>)
          : summaryChips(s).length ? summaryChips(s) : <Chip tone="good">nothing to fix</Chip>}
      </div>
      <button
        type="button"
        onClick={() => onOpen(area.id)}
        className="mt-auto h-[34px] rounded-[9px] border text-[12.5px] font-medium"
        style={tone ? { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#0A0A0A', fontWeight: 600 } : { borderColor: 'var(--surf-line-strong)' }}
      >
        {noAgent ? 'Review devices' : `Open ${area.name}`}
      </button>
    </div>
  );
};

// Cards hang off one bus line from the router.
const AreaRow = ({ areas, onOpen }) => (
  <div className="flex gap-4 overflow-x-auto pb-1">
    {areas.map((area, i) => (
      <div key={area.id} className="relative pt-7 flex-[1_1_190px] min-w-[190px]">
        <span aria-hidden="true" className="absolute top-0 h-[1.5px]" style={{ left: i === 0 ? '50%' : -8, right: i === areas.length - 1 ? '50%' : -8, background: LINE, borderTop: area.id === NO_AGENT_AREA ? `1.5px dotted ${LINE}` : undefined, height: area.id === NO_AGENT_AREA ? 0 : undefined }} />
        <span aria-hidden="true" className="absolute top-0 left-1/2 w-[1.5px] h-7" style={{ background: area.id === NO_AGENT_AREA ? 'transparent' : LINE, borderLeft: area.id === NO_AGENT_AREA ? `1.5px dotted ${LINE}` : undefined }} />
        <AreaCard area={area} onOpen={onOpen} />
      </div>
    ))}
  </div>
);

const AreaView = ({ area, others, tree, matches, selectedId, onSelect, expanded, onToggle, editing, onEdit, onUpdateAp, onOpen }) => {
  const props = { tree, matches, selectedId, onSelect, expanded, onToggle };
  const areaNames = tree.areas.filter(a => a.id !== NO_AGENT_AREA).map(a => a.name);
  const s = area.summary;
  return (
    <div className="flex flex-wrap gap-5 items-start">
      <div className="flex-[999_1_560px] min-w-0 flex flex-col gap-5 p-5 rounded-[18px] border" style={{ borderColor: 'var(--surf-line-strong)', background: 'var(--surf-bg)' }}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[12px] tracking-[0.1em] font-semibold">{area.name.toUpperCase()}</span>
          <span className="text-[12px]" style={{ color: 'var(--ink-faint)' }}>
            {s.total} device{s.total === 1 ? '' : 's'}{s.findings ? ` · ${s.findings} finding${s.findings === 1 ? '' : 's'}` : ''}{s.weak ? ` · ${s.weak} weak signal` : ''}{s.late ? ` · ${s.late} late` : ''}
          </span>
          <button type="button" onClick={() => onOpen(null)} className="ml-auto h-8 flex items-center gap-1.5 px-3 rounded-lg border text-[12.5px]" style={{ borderColor: 'var(--surf-line-strong)' }}>
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />All areas
          </button>
        </div>
        <div className="grid gap-x-6 gap-y-8" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {area.aps.map(ap => (
            <Group key={ap.key} id={`ap:${ap.key}`} line={ap.kind === 'switch' ? 'solid' : 'dashed'} members={ap.clients} {...props}
              head={<ApHead ap={ap} areaNames={areaNames} editing={editing === ap.key} onEdit={onEdit} onUpdate={onUpdateAp} onSelect={onSelect} />} />
          ))}
          {area.wired.length > 0 && (
            <Group id={`wired:${area.id}`} line="solid" members={area.wired} {...props}
              head={<PlainHead icon={Cable} title="Wired" sub={`Cabled to the network · ${area.wired.length}`} />} />
          )}
          {area.unlinked.length > 0 && (
            <Group id={`unlinked:${area.id}`} line="dotted" members={area.unlinked} {...props}
              head={<PlainHead icon={CircleHelp} dashed title="Link not reported yet" sub="Reports after its agent updates" />} />
          )}
          {area.groups.map(g => (
            <Group key={g.name} id={`cat:${g.name}`} line="dotted" members={g.members} {...props}
              head={<PlainHead icon={CATEGORY_ICON[g.name] || CircleHelp} dashed title={`${g.name} · ${g.members.length}`} />} />
          ))}
        </div>
      </div>
      {others.length > 0 && (
        <aside aria-label="Other areas" className="flex-[1_1_220px] max-w-full flex flex-col gap-2.5 p-3.5 rounded-[18px] border" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}>
          <span className="font-mono text-[11px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>OTHER AREAS</span>
          {others.map(o => (
            <button key={o.id} type="button" onClick={() => onOpen(o.id)} className="flex flex-col gap-1.5 p-3 rounded-xl border text-left" style={{ background: o.id === NO_AGENT_AREA ? 'transparent' : 'var(--surf-surface)', borderColor: o.suspicious ? 'var(--sev-crit-fg)' : 'var(--surf-line)', borderStyle: o.id === NO_AGENT_AREA ? 'dashed' : 'solid' }}>
              <span className="flex justify-between gap-2 text-[13px] font-medium"><span className="truncate">{o.name}</span><span className="font-mono font-normal" style={{ color: 'var(--ink-faint)' }}>{o.summary.total}</span></span>
              {o.id !== NO_AGENT_AREA && <RiskMix s={o.summary} />}
              <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>
                {o.id === NO_AGENT_AREA ? o.groups.map(g => `${g.name} ${g.members.length}`).join(' · ') : [o.aps.length && `${o.aps.length} AP${o.aps.length === 1 ? '' : 's'}`, o.summary.findings && `${o.summary.findings} finding${o.summary.findings === 1 ? '' : 's'}`, o.summary.weak && `${o.summary.weak} weak`, o.summary.late && `${o.summary.late} late`].filter(Boolean).join(' · ') || 'nothing to fix'}
              </span>
            </button>
          ))}
        </aside>
      )}
    </div>
  );
};

// ── Alerts above the map ───────────────────────────────────────────────────
const Alerts = ({ tree, onUpdateAp, onShowAp, onSelect }) => {
  const items = [];
  tree.suspicious.slice(0, 2).forEach(ap => items.push(
    <div key={ap.key} role="alert" className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border" style={{ borderColor: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
      <ShieldAlert className="w-[18px] h-[18px] shrink-0" style={{ color: 'var(--sev-crit-fg)' }} strokeWidth={2} />
      <span className="flex-[1_1_320px] text-[13px] leading-snug">
        <b>Unknown access point using “{ap.ssids.join('”, “')}”.</b>{' '}
        <span style={{ color: 'var(--ink-muted)' }}>{ap.clients.length ? `${ap.clients.map(c => c.host).slice(0, 3).join(', ')} joined it through ${ap.key}.` : `Seen at ${ap.key}.`} It isn't one of the access points you accepted.</span>
      </span>
      <button type="button" onClick={() => onShowAp(ap)} className="h-8 px-3 rounded-lg text-[12.5px] font-semibold" style={{ background: 'var(--sev-crit-fg)', color: '#0A0A0A' }}>Show</button>
      <button type="button" onClick={() => onUpdateAp(ap.key, { status: 'known' })} className="h-8 px-3 rounded-lg border text-[12.5px]" style={{ borderColor: 'var(--surf-line-strong)' }}>It's ours</button>
    </div>,
  ));
  tree.mismatched.slice(0, 2).forEach(a => items.push(
    <div key={`gw-${a.id}`} role="alert" className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border" style={{ borderColor: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
      <ShieldAlert className="w-[18px] h-[18px] shrink-0" style={{ color: 'var(--sev-crit-fg)' }} strokeWidth={2} />
      <span className="flex-[1_1_320px] text-[13px] leading-snug">
        <b>{a.host} reaches the internet through a different gateway.</b>{' '}
        <span style={{ color: 'var(--ink-muted)' }}>It uses {tree.linkOf(a.id).gateway_ip}{tree.linkOf(a.id).gateway_mac ? ` (${tree.linkOf(a.id).gateway_mac})` : ''}; your other devices use {gatewayFor(tree, a.location)?.ip}. This can mean a rogue access point or ARP spoofing.</span>
      </span>
      <button type="button" onClick={() => onSelect(a.id, true)} className="h-8 px-3 rounded-lg text-[12.5px] font-semibold" style={{ background: 'var(--sev-crit-fg)', color: '#0A0A0A' }}>Show</button>
    </div>,
  ));
  if (tree.knownCount === 0 && tree.newCount > 0) {
    const fresh = tree.accessPoints.filter(ap => ap.status !== 'known');
    items.push(
      <div key="accept" className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border" style={{ borderColor: 'var(--surf-line-strong)', background: 'var(--surf-raised)' }}>
        <Wifi className="w-[18px] h-[18px] shrink-0" style={{ color: 'var(--accent)' }} strokeWidth={2} />
        <span className="flex-[1_1_320px] text-[13px] leading-snug">
          <b>{fresh.length} access point{fresh.length === 1 ? '' : 's'} found.</b>{' '}
          <span style={{ color: 'var(--ink-muted)' }}>Accept the ones that are yours, so a new one using the same network name is flagged as possibly rogue.</span>
        </span>
        <button type="button" onClick={() => fresh.forEach(ap => onUpdateAp(ap.key, { status: 'known' }))} className="h-8 px-3 rounded-lg border text-[12.5px] font-medium" style={{ borderColor: 'var(--surf-line-strong)' }}>Accept all {fresh.length}</button>
      </div>,
    );
  }
  return items.length ? <div className="flex flex-col gap-2.5">{items}</div> : null;
};

// ── The map ────────────────────────────────────────────────────────────────
const TopologyMap = ({ assets, topo, matches, selectedId, onSelect, linkHours, onLinkHours, focus, onUpdateAp, internetPeers }) => {
  const tree = useMemo(() => buildTopology(assets, topo), [assets, topo]);
  const [openId, setOpenId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const open = tree.areas.find(a => a.id === openId) || null;

  const toggle = (id) => setExpanded(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const reveal = (id) => {
    const area = tree.areaOf(id);
    if (!area) return;
    setOpenId(area);
    const group = groupOf(tree, id);
    if (group) setExpanded(s => new Set(s).add(group));
    setTimeout(() => document.getElementById(`nm-dev-${id}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 50);
  };

  // Search and "Show" open the device's area and its list.
  useEffect(() => { if (focus?.id) reveal(focus.id); }, [focus]); // eslint-disable-line react-hooks/exhaustive-deps

  const showAp = (ap) => { setOpenId(tree.areas.find(a => a.aps.includes(ap))?.id || null); setExpanded(s => new Set(s).add(`ap:${ap.key}`)); };
  const select = (id, show) => { onSelect(id); if (show && id) reveal(id); };

  // The routers drawn above the areas: those of the open area, else each location's main one.
  // A router nobody has reported yet is a placeholder, as before.
  const mainRouters = tree.gateways.filter(g => g.primary);
  const severalSites = new Set(mainRouters.map(g => g.location)).size > 1;
  const shownRouters = open ? open.gateways : severalSites ? mainRouters : mainRouters.slice(0, 1);
  const routerNode = (g) => {
    const name = g.asset ? (g.asset.unnamed ? g.asset.ip : g.asset.host) : g.ip;
    const sub = [
      g.asset?.vendor || 'Router',
      g.primary ? `gateway for ${g.reporters} of ${g.total} device${g.total === 1 ? '' : 's'}` : `second gateway · ${g.reporters} devices`,
      severalSites && g.location ? g.location : null,
    ].filter(Boolean).join(' · ');
    return { key: `${g.location ?? ''}|${g.ip}|${g.mac || ''}`, name, sub, asset: g.asset };
  };
  const routerNodes = shownRouters.length
    ? shownRouters.map(routerNode)
    : [{ key: 'none', name: null, sub: 'Agents report their gateway with their next heartbeat', asset: null }];

  return (
    <section aria-label="Topology" className="h-full min-h-[560px] flex flex-col rounded-[14px] border overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b" style={{ borderColor: 'var(--surf-line)' }}>
        <nav aria-label="Map level" className="flex items-center gap-2 text-[13.5px] min-w-0">
          {open
            ? <><button type="button" onClick={() => setOpenId(null)} className="hover:underline" style={{ color: 'var(--ink-muted)' }}>All areas</button><ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ink-faint)' }} /><span className="font-semibold truncate">{open.name}</span></>
            : <><span className="font-semibold">All areas</span><span className="text-[12.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{tree.areas.length} areas · {tree.accessPoints.length} access point{tree.accessPoints.length === 1 ? '' : 's'}</span></>}
        </nav>
        <div role="group" aria-label="Traffic window" className="flex items-center gap-0.5 py-[3px] pr-[3px] pl-2.5 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
          <span className="text-[12px] mr-1" style={{ color: 'var(--ink-faint)' }}>Traffic</span>
          {TRAFFIC_WINDOWS.map(([h, label]) => (
            <button key={h} type="button" onClick={() => onLinkHours(h)} aria-pressed={linkHours === h} className="h-7 px-2.5 rounded-[7px] border text-[12.5px]" style={seg(linkHours === h)}>{label}</button>
          ))}
        </div>
      </div>

      <div className="flex-grow min-h-0 overflow-auto px-5 py-5 flex flex-col">
        <Alerts tree={tree} onUpdateAp={onUpdateAp} onShowAp={showAp} onSelect={select} />

        {assets.length === 0 ? (
          <p className="m-auto font-mono text-xs" style={{ color: 'var(--ink-faint)' }}>No assets yet. Add one, or run the agent with --discover.</p>
        ) : (
          <>
            {/* Backbone */}
            <div className="flex flex-col items-center pt-4">
              <Node icon={Globe} tone="accent" title="Internet" sub={internetPeers ? `${internetPeers} public IPs · last ${linkHours === 168 ? '7 days' : '24 h'}` : 'No traffic reported yet'} ariaLabel="Internet" />
              <VLine />
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
                {routerNodes.map(r => (
                  <Node key={r.key} icon={Router} title={r.name || 'Router'} sub={r.sub} ariaLabel={`Router ${r.name || 'not known yet'}`} onClick={r.asset ? () => onSelect(r.asset.id) : undefined} />
                ))}
                {tree.networkDevices.slice(0, 3).map(d => (
                  <span key={d.id} className="flex items-center gap-3">
                    <span aria-hidden="true" className="w-6 h-[1.5px]" style={{ background: LINE }} />
                    <Node icon={Network} dashed={d.discovered} title={d.unnamed ? d.ip : d.host} sub={[d.vendor || 'Network device', d.discovered ? 'needs review' : d.ip].join(' · ')} ariaLabel={`${d.host}, network device`} onClick={() => onSelect(d.id)} />
                  </span>
                ))}
                {tree.networkDevices.length > 3 && <span className="text-[12px]" style={{ color: 'var(--ink-faint)' }}>+ {tree.networkDevices.length - 3} more network devices</span>}
              </div>
              <VLine />
            </div>
            {open
              ? <AreaView area={open} others={tree.areas.filter(a => a.id !== open.id)} tree={tree} matches={matches} selectedId={selectedId} onSelect={onSelect}
                  expanded={expanded} onToggle={toggle} editing={editing} onEdit={setEditing} onUpdateAp={onUpdateAp} onOpen={setOpenId} />
              : <AreaRow areas={tree.areas} onOpen={setOpenId} />}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3 border-t text-[12px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
        <span className="flex items-center gap-1.5">{['crit', 'high', 'med', 'low'].map(b => <span key={b} className="w-2.5 h-2.5 rounded-[3px]" style={{ background: bandFg(b) }} />)}<span className="ml-1">Risk score</span></span>
        <span className="flex items-center gap-2"><span className="w-5 border-t-[1.5px] border-dashed" style={{ borderColor: 'var(--ink-faint)' }} />Wi-Fi</span>
        <span className="flex items-center gap-2"><span className="w-5 h-[1.5px]" style={{ background: 'var(--ink-faint)' }} />Wired</span>
        <span className="flex items-center gap-2"><span className="w-5 border-t-[1.5px] border-dotted" style={{ borderColor: 'var(--ink-faint)' }} />Link unknown</span>
        <span className="flex items-center gap-2"><Bars dbm={-60} />Signal</span>
        <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 box-border rounded border border-dashed" style={{ borderColor: 'var(--ink-faint)' }} />Not accepted / no agent</span>
      </div>
    </section>
  );
};

export default TopologyMap;
